import type { AccessibilityFeatures, Course } from '@/lib/courses/types'
import type { NeedsCategory, NeedsProfile } from './types'

export interface ScoredCourse {
  course: Course
  /** Ukupan score za rangiranje (feature-poklapanje + bonus za početni nivo). */
  score: number
  /** Score samo od a11y feature-poklapanja — koristi se za "ima li relevantnosti". */
  featureScore: number
  /** Kratki razlozi preporuke (za UI "zašto baš ovaj kurs"). */
  reasons: string[]
}

type FeatureKey = keyof AccessibilityFeatures

interface FeatureWeight {
  feature: FeatureKey
  weight: number
  reason: string
}

/** Koje a11y feature kursa su poželjne za koju potrebu, sa težinom i razlogom. */
const NEED_FEATURE_WEIGHTS: Partial<Record<NeedsCategory, FeatureWeight[]>> = {
  'deaf-hoh': [
    { feature: 'captions', weight: 3, reason: 'Ima titlove' },
    { feature: 'signLanguage', weight: 3, reason: 'Ima znakovni jezik' },
  ],
  blindness: [{ feature: 'audioDescription', weight: 3, reason: 'Ima audio opis' }],
  'low-vision': [{ feature: 'audioDescription', weight: 2, reason: 'Ima audio opis' }],
  dyslexia: [
    { feature: 'easyRead', weight: 3, reason: 'Dostupan u verziji za lako čitanje' },
    { feature: 'audioDescription', weight: 1, reason: 'Ima audio opis' },
  ],
  intellectual: [{ feature: 'easyRead', weight: 3, reason: 'Dostupan u verziji za lako čitanje' }],
  autism: [{ feature: 'easyRead', weight: 2, reason: 'Dostupan u verziji za lako čitanje' }],
  adhd: [{ feature: 'easyRead', weight: 2, reason: 'Dostupan u verziji za lako čitanje' }],
}

const BEGINNER_BONUS = 1

/** Da li profil sadrži kategorije kojima početni nivo posebno odgovara. */
function prefersBeginner(profile: NeedsProfile): boolean {
  return profile.categories.some((c) => c === 'intellectual' || c === 'dyslexia')
}

interface CourseScore {
  /** Score samo od poklapanja a11y feature-a — pravi "signal" relevantnosti. */
  featureScore: number
  /** Ukupan score za rangiranje: feature-poklapanje + bonus za početni nivo. */
  score: number
  reasons: string[]
}

function scoreCourse(course: Course, profile: NeedsProfile): CourseScore {
  let featureScore = 0
  const reasons: string[] = []
  const seenFeatures = new Set<FeatureKey>()

  for (const category of profile.categories) {
    const weights = NEED_FEATURE_WEIGHTS[category]
    if (!weights) continue
    for (const { feature, weight, reason } of weights) {
      if (!course.accessibilityFeatures[feature]) continue
      featureScore += weight
      // razlog dodaj samo jednom po feature-u, čak i ako više potreba traži isti
      if (!seenFeatures.has(feature)) {
        seenFeatures.add(feature)
        reasons.push(reason)
      }
    }
  }

  // Uzrast se namjerno ne uzima u obzir: platforma ne pita korisnika za godine,
  // pa rangiranje stoji samo na tome šta kurs nudi (a11y feature-i) i koliko je
  // zahtjevan.
  let score = featureScore

  if (prefersBeginner(profile) && course.difficulty === 'beginner') {
    score += BEGINNER_BONUS
  }

  return { featureScore, score, reasons }
}

/**
 * Čista funkcija: rangira kurseve po profilu potreba.
 * - Sortiranje: score desc, pa featured, pa abecedno po naslovu (stabilno/deterministički).
 * - Fallback: ako profil nema signala (prazan, ili svi score === 0) → featured prvi, pa abecedno.
 */
export function rankCourses(profile: NeedsProfile, courses: readonly Course[]): ScoredCourse[] {
  const scored: ScoredCourse[] = courses.map((course) => {
    const { featureScore, score, reasons } = scoreCourse(course, profile)
    return { course, featureScore, score, reasons }
  })

  // Rangiranje uvažava i bonus za početni nivo; kad nema baš nikakvog score-a,
  // pada na featured + abecedno (deterministički fallback).
  const hasSignal = scored.some((s) => s.score > 0)

  return [...scored].sort((a, b) => {
    if (hasSignal && b.score !== a.score) return b.score - a.score
    if (a.course.featured !== b.course.featured) return a.course.featured ? -1 : 1
    return a.course.title.localeCompare(b.course.title, 'sr')
  })
}

/** Pomoćnik: vrati top N preporuka koje imaju bar nešto poklapanja (score > 0). */
export function topRecommendations(
  profile: NeedsProfile,
  courses: readonly Course[],
  limit = 3,
): ScoredCourse[] {
  const ranked = rankCourses(profile, courses)
  const withSignal = ranked.filter((s) => s.featureScore > 0)
  const pool = withSignal.length > 0 ? withSignal : ranked
  return pool.slice(0, limit)
}
